import {
	AWSConnectProctorSharedEvents,
	AWSLoginProctorRequest,
	ConnectAgentDetails,
	ContactSessionAttributes,
	ContactState,
	ExchangeCredentialResponse,
	Patient,
	Person
} from "@emed-labs/virtual-lab-models";
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { UseEventAddRemoveListenerMethods, useEventListener } from "../hooks/UseEventListener";

import { ExecutorUtils } from "@emed-labs/platform-utility-common-utils";
import { LoveClientError } from "@emed-labs/platform-utility-generic-clients";
import { useStateRef } from "../hooks/UseStateRef";
import { AgentUIExtraNavigatorAttributes } from "../models/Models";
import { AuthContext } from "./AuthProvider";
import { EnvironmentContext } from "./EnvironmentProvider";
import { ModalContext } from "./ModalProvider";
import { VLabAPIContext } from "./VLabAPIProvider";
import { Config } from "../environment/config";
import { useLogger } from "./LogProvider";

export interface AWSConnectAgentChildContext extends UseEventAddRemoveListenerMethods {
	sendChatMessage: (content: string) => Promise<void>;
	patientData?: Patient;
	contactAttributes?: Partial<ContactSessionAttributes>;
	patientRoleActorData?: Patient | Person;
	contactID?: string;
	contactActive: boolean;
	contactState?: ContactState;
	loadingContact: boolean;
	defaultAttributes?: AgentUIExtraNavigatorAttributes;
	initiateProctorLogin: (params?: any) => Promise<void>;
}

export const AWSConnectAgentChildContext = createContext<AWSConnectAgentChildContext>(
	{} as AWSConnectAgentChildContext
);
AWSConnectAgentChildContext.displayName = "AWSConnectContext";

export const AWSConnectAgentChildProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const AuthCtx = useContext(AuthContext);
	const Modal = useContext(ModalContext);
	const VLabAPICtx = useContext(VLabAPIContext);
	const EnvCtx = useContext(EnvironmentContext);
	const logger = useLogger("providers.AWSConnectAgentChildProvider");

	const { addListener, removeListener, dispatchEvent } = useEventListener(
		AWSConnectProctorSharedEvents,
		EnvCtx.get(Config.GLOBAL_PROCTOR_UI_ORIGIN).split(",")
	);

	const [defaultAttributes, setDefaultAttributes, defaultAttributesRef] = useStateRef<AgentUIExtraNavigatorAttributes>({
		AGENT_NAME: "",
		AGENT_USERNAME: "",
		AGENT_TOKEN: ""
	});

	const [contactID, setContactID, contactIDRef] = useStateRef<string>();
	const [contactState, setContactState] = useState<ContactState>();
	const [contactActive, setContactActive] = useState(false);
	const [contactAttributes, setContactAttributes, contactAttributesRef] =
		useStateRef<Partial<ContactSessionAttributes>>();

	const [patientData, setPatientData] = useState<Patient>();
	const [patientRoleActorData, setPatientRoleActorData] = useState<Patient | Person>();

	const [loadingContact, setLoadingContact] = useState<boolean>(false);
	const [loggingIn, setLoggingIn, loggingInRef] = useStateRef<boolean>(false);
	const [loginComplete, setLoginComplete] = useStateRef<boolean>(false);

	/**
	 * Wait a cycle, ensure we have a token.
	 * Then let parent glboal proctor know we are logged in.
	 */
	useEffect(() => {
		if (loginComplete && AuthCtx.token) {
			logger.debug("useEffect", "Login is complete. Sending PROCTOR_CHILD_LOGGED_IN to parent.");
			dispatchEvent(AWSConnectProctorSharedEvents.PROCTOR_CHILD_LOGGED_IN, null, window.parent);
		}
	}, [loginComplete, AuthCtx.token]);

	useEffect(() => {
		/**
		 * listens via window post message to global proctor
		 */
		return addListener(AWSConnectProctorSharedEvents.INITIATE_PROCTOR_LOGIN, async (data) => {
			logger.debug("INITIATE_PROCTOR_LOGIN", "CHILD: received INITIATE_PROCTOR_LOGIN. Attempting login");
			proctorLogin(data);
		});
	}, []);

	useEffect(() => {
		/**
		 * listens via window post message to global proctor
		 */
		return addListener(AWSConnectProctorSharedEvents.CONTACT_ACCEPTED, async (data) => {
			logger.debug("CONTACT_ACCEPTED", "CHILD: received CONTACT_SWITCHED. Attempting login", data);
			proctorLogin(data);
		});
	}, []);

	useEffect(() => {
		/**
		 * listens via window post message to global proctor
		 */
		return addListener(AWSConnectProctorSharedEvents.CONTACT_ENDED, async (data) => {
			logger.debug("CONTACT_ENDED", "CHILD: received CONTACT_ENDED. ");
			setContactID(data.contactID);
			setContactState(data.contactState);
		});
	}, []);

	useEffect(() => {
		/**
		 * listens via window post message to global proctor
		 */
		return addListener(AWSConnectProctorSharedEvents.CONTACT_DESTROYED, async (data) => {
			logger.debug("CONTACT_DESTROYED", "CHILD: received CONTACT_DESTROYED. ");
			onContactCleared();
		});
	}, []);

	const onContactCleared = () => {
		logger.debug("onContactCleared", "Logging out.");
		AuthCtx.logout();

		setContactID(undefined);
		setPatientData(undefined);
		setPatientRoleActorData(undefined);
		setContactAttributes(undefined);
		setContactState(undefined);
		setContactActive(false);
	};

	const initiateProctorLogin = async (params?: any) => {
		logger.debug("initiateProctorLogin", "Dispatching PROCTOR_CHILD_READY.");
		dispatchEvent!(AWSConnectProctorSharedEvents.PROCTOR_CHILD_READY, {}, window.parent);
	};

	const proctorLogin = async ({ contactID, agentEmail, agentToken, contactState, agentName }: ConnectAgentDetails) => {
		try {
			if (loggingInRef.current) return;
			setLoggingIn(true);
			setLoginComplete(false);

			logger.debug("proctorLogin", "Initiating AWS Connect proctor login", {
				contactID,
				agentEmail,
				agentToken,
				contactState,
				agentName
			});
			const credential = await VLabAPICtx.call<AWSLoginProctorRequest, ExchangeCredentialResponse>(
				"POST",
				"/v1/aws-connect/proctor/$login",
				{
					data: {
						contactID,
						agentToken,
						agentEmail
					}
				}
			);

			const token = await AuthCtx.setCredential(credential);

			logger.info("proctorLogin", "Logged in. Joining AWS Connect", {
				contactID,
				agentEmail,
				agentToken,
				contactState,
				agentName
			});

			const contactInfo = await ExecutorUtils.retryWithBackoff(
				() =>
					VLabAPICtx.call("POST", `/v1/aws-connect/proctor/${contactID}/$join`, {
						headers: {
							"x-api-key": token
						}
					}),
				250,
				2
			).promise();

			setDefaultAttributes({
				AGENT_NAME: agentName,
				AGENT_USERNAME: agentEmail,
				AGENT_TOKEN: agentToken
			});
			setContactID(contactID);
			setPatientData(contactInfo.patient);
			setPatientRoleActorData(contactInfo.patientRoleActor);
			setContactAttributes(contactInfo.attributes);
			setLoadingContact(false);
			setContactState(contactState);
			setContactActive(true);
			setLoginComplete(true);
			setLoggingIn(false);

			logger.info("proctorLogin", "proctorLogin success");
		} catch (e) {
			setLoggingIn(false);
			const error = e as LoveClientError;
			logger.error("proctorLogin", `Unable to login as proctor: ${e.message}`, e, { response: error.response });
			const message = error.response?.data?.message || error.message;
			Modal.alert(`An error occurred logging you in as a proctor. ${message}`);
		}
	};

	const sendChatMessage = async (content: string) => {
		/**
		 * Send this event up from iframe via post message to parent, who sends message
		 */
		dispatchEvent(AWSConnectProctorSharedEvents.PROCTOR_GLOBAL_SEND_MESSAGE, content, window.parent);
	};

	const value: AWSConnectAgentChildContext = {
		sendChatMessage,
		initiateProctorLogin,
		patientData,
		contactAttributes,
		patientRoleActorData,
		contactID,
		contactState,
		loadingContact,
		defaultAttributes,
		addListener,
		removeListener,
		contactActive
	};
	return <AWSConnectAgentChildContext.Provider value={value}>{props.children}</AWSConnectAgentChildContext.Provider>;
};
